import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatCheckbox, MatCheckboxChange, MatDialog, Sort } from '@angular/material';
import { AssessmentAnswerInterface } from 'app/assessment/models';
import { UserInterface } from 'app/core/user/models';
import { InstalledStateEnum } from 'app/equipment/enums/installed-state.enum';
import { EmployeeEquipmentWorkflowInterface } from 'app/equipment/models';
import { EquipmentService } from 'app/equipment/services';
import { objectCompare, sortEmployeeEquipmentWorkflowsByProp } from 'app/shared/utilities/object-sort-methods.utility';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { EquipmentDialogComponent } from '../equipment-dialog/equipment-dialog.component';

@Component({
  selector: 'cz-workflows-view',
  templateUrl: './workflows-view.component.html',
  styleUrls: ['./workflows-view.component.scss'],
})
export class WorkflowsViewComponent implements OnInit, OnDestroy {
  @Input()
  set workflowList(value: EmployeeEquipmentWorkflowInterface[]) {
    this.exportForm = this._formBuilder.group(this.createExportFormControls(value));
    this._workflowList = value;
    if(value && value.length > 0) {
      this.filterData();
      this.sortData(this.sortState);
    } else {
      this.filteredEquipment = value;
    }
  }
  get workflowList(): EmployeeEquipmentWorkflowInterface[] {
    return this._workflowList;
  }

  @Input() public userId: number;
  @Input() public esppId: number;
  @Input() public viewState: string;
  @Input() public readonly: boolean;
  @Input() public authUser: UserInterface;

  public installedView: boolean;

  public itemSearchControl: FormControl;
  public workAreaSelectControl: FormControl;
  public workAreas: AssessmentAnswerInterface[];

  public filteredEquipment: EmployeeEquipmentWorkflowInterface[];

  public checkBoxSortState: Sort = { active: 'none', direction: '' };
  @ViewChild('selectAllCheckbox') public selectAllCheckbox: MatCheckbox;
  public exportForm: FormGroup;
  public showExportErrorMessage: boolean = false;
  public exportUrl: string = 'api/equipment/export';

  public sortState: Sort = { active: 'item', direction: 'asc' };

  private _workflowList: EmployeeEquipmentWorkflowInterface[];
  private readonly destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(public dialog: MatDialog,
              private _formBuilder: FormBuilder,
              private _equipmentService: EquipmentService) {
    this.itemSearchControl = new FormControl('');
    this.workAreaSelectControl = new FormControl('');
  }

  public ngOnInit(): void {
    this.installedView = this.viewState === InstalledStateEnum.StateInstalled;

    this.workAreaSelectControl.setValue(-1);
    this._equipmentService.getWorkAreaValues()
      .pipe(takeUntil(this.destroy$))
      .subscribe((workAreaData: AssessmentAnswerInterface[]) => {
        if(!workAreaData) { return; }

        this.workAreas = workAreaData;
      });

    this.itemSearchControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.filterData();
      });

    this.workAreaSelectControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.filterData();
      });

  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public checkUncheckAll(event: MatCheckboxChange): void {
    this.workflowList
    .forEach((workFlow: EmployeeEquipmentWorkflowInterface) => {
      this.exportForm.get(workFlow.id.toString()).setValue(event.checked);
    });
  }

  public sortChecked(): void {
    if(this.checkBoxSortState.active === 'none') {
      this.checkBoxSortState.active = 'checked';
      this.checkBoxSortState.direction = 'desc';
    } else if(this.checkBoxSortState.direction === 'desc') {
      this.checkBoxSortState.direction = 'asc';
    } else {
      this.checkBoxSortState.active = 'none';
    }

    this.sortData(this.checkBoxSortState);
  }

  public sortData(sort: Sort): void {
    this.sortState = sort;
    const isAsc: boolean = sort.direction === 'asc';

    if(sort.active === 'checked') {
      this.filteredEquipment.sort((a: EmployeeEquipmentWorkflowInterface, b: EmployeeEquipmentWorkflowInterface) => {
        const aNum: number = this.exportForm.get(a.id.toString()).value ? 1 : 0;
        const bNum: number = this.exportForm.get(b.id.toString()).value ? 1 : 0;
        return objectCompare(aNum, bNum, isAsc);
      });
    } else {
      this.filteredEquipment = sortEmployeeEquipmentWorkflowsByProp(this.filteredEquipment, sort.active, isAsc);
    }
  }

  public openEquipmentDialog(workFlow: EmployeeEquipmentWorkflowInterface): void {
    if(!this.authUser) { return; }
    let dialogWorkflow: EmployeeEquipmentWorkflowInterface;
    if(!workFlow) {
      dialogWorkflow = {
        forUser: {
          id: this.userId,
        },
        esppId: this.esppId,
      };
    } else {
      dialogWorkflow = workFlow;
    }
    this.dialog.open(EquipmentDialogComponent, {
      height: '700px',
      width: '800px',
      panelClass: 'equipment-dialog-component',
      data: {
        workFlow: dialogWorkflow,
        origin: this.installedView ? InstalledStateEnum.StateInstalled : InstalledStateEnum.StateRecommended,
        authUser: this.authUser,
        esppId: this.esppId,
        workAreas: this.workAreas,
      },
    });
  }

  public doBulkAction(pdf: boolean): void {
    this.showExportErrorMessage = false;
    const keys: string[] = Object.keys(this.exportForm.value);

    // filters all keys with true value
    const keyArray: string[] = keys.filter((key: string) => {
      return this.exportForm.value[key];
    });

    let workFlowIdArray: number[];

    if (keyArray.length < 1) {
      this.showExportErrorMessage = true;
      return;
    }

    this.showExportErrorMessage = false;
    workFlowIdArray = keyArray.map((id: string) => Number(id));

    let exportUrl: string = this.installedView ?
      `api/users/${this.userId}/equipment/export/installed` :
      `api/users/${this.userId}/equipment/export/recommended`;

    exportUrl = pdf ? 'api/pdf/equipment_pdf/' + this.userId : exportUrl;

    this._equipmentService.getEquipmentExportExcel(workFlowIdArray, exportUrl)
      .pipe(take(1))
      .subscribe((exportData: { blob: Blob, fileName: string }) => {
        if (!exportData) { return; }

        const url: string = window.URL.createObjectURL(exportData.blob);
        const a: HTMLAnchorElement = document.createElement('a');
        document.body.appendChild(a);
        a.setAttribute('style', 'display: none');
        a.href = url;
        a.download = exportData.fileName;
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      });
  }

  private createExportFormControls(workFlows: EmployeeEquipmentWorkflowInterface[]): {} {
    const group: { [id: number]: FormControl } = {};
    if (!workFlows) { return group; }

    workFlows.forEach((workFlow: EmployeeEquipmentWorkflowInterface) => {
      group[workFlow.id] = new FormControl(false);
    });

    return group;
  }

  private filterData(): void {
    const workAreaFilter: boolean = this.workAreaSelectControl.value > 0;
    this.filteredEquipment = this.workflowList.filter((workFlow: EmployeeEquipmentWorkflowInterface) => {
      if(workFlow.equipmentItem.title.toLowerCase().indexOf(this.itemSearchControl.value.toLowerCase()) >= 0) {
        if(workAreaFilter) {
          if(workFlow.equipmentItem.workAreaId === this.workAreaSelectControl.value) {
            return workFlow;
          }
        } else {
          return workFlow;
        }
      }
    });

    if(this.readonly) {
      const draftFilteredEquipment: EmployeeEquipmentWorkflowInterface[] =
        this.filteredEquipment.filter((workflow: EmployeeEquipmentWorkflowInterface) => !workflow.isDraft);
      this.filteredEquipment = draftFilteredEquipment;
    }
  }
}
